import { useCallback, useEffect, useRef } from "react";

/**
 * `Hooks/useDebouncedCallback` plus a cancel on unmount: a search typed just
 * before opening a document must not write `?q=` after the list is gone
 * (react-router would navigate back to the list).
 */
export const useDebounced = <T extends unknown[]>(
  callback: (...args: T) => void,
  delay: number,
) => {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestCallback = useRef(callback);

  useEffect(() => {
    latestCallback.current = callback;
  }, [callback]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return useCallback(
    (...args: T) => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        latestCallback.current(...args);
      }, delay);
    },
    [delay],
  );
};
